/**
 * Date Formatting Utilities
 * Centralized date/time formatting functions with i18n support
 */

import i18n from '../i18n';

/**
 * Get the locale string for Intl APIs based on current language.
 * We fall back to 'en-US' for Uzbek because the uz-UZ locale is poorly
 * supported in many browsers (produces "M03" instead of month names).
 * Uzbek-specific formatting is handled via the month-name maps below.
 */
const getIntlLocale = () => {
  const lang = i18n.language || 'uz';
  const map = { ru: 'ru-RU', en: 'en-US' };
  return map[lang] || 'en-US';
};

// Keep the old name as alias for backward compatibility inside this module
const getLocale = getIntlLocale;

const UZ_MONTHS_SHORT = [
  'Yan', 'Fev', 'Mar', 'Apr', 'May', 'Iyun',
  'Iyul', 'Avg', 'Sen', 'Okt', 'Noy', 'Dek',
];
const UZ_MONTHS_LONG = [
  'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
  'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
];

/**
 * Safely format a date with locale, providing Uzbek month names manually.
 */
const formatLocalDate = (date, options) => {
  const d = new Date(date);
  const lang = i18n.language || 'uz';

  if (lang === 'uz') {
    const day = d.getDate();
    const monthIdx = d.getMonth();
    const year = d.getFullYear();
    const monthName = options.month === 'long' ? UZ_MONTHS_LONG[monthIdx] : UZ_MONTHS_SHORT[monthIdx];

    if (options.year) {
      return `${day} ${monthName}, ${year}`;
    }
    return `${day} ${monthName}`;
  }

  return d.toLocaleDateString(getLocale(), options);
};

/**
 * Format a date to full datetime string
 * @param {string|Date} fetchedDate - The date to format
 * @returns {string} Formatted date string (YYYY-MM-DD HH:MM:SS)
 */
export const formatDate = (fetchedDate) => {
  const date = new Date(fetchedDate);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}   ${hours}:${minutes}:${seconds}`;
};

/**
 * Format a date for display (short format)
 */
export const formatDateShort = (date) => {
  return formatLocalDate(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date for display (medium format)
 */
export const formatDateMedium = (date) => {
  return formatLocalDate(date, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format day + short month for schedule headers.
 */
export const formatMonthDay = (date) => {
  return formatLocalDate(date, {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format time only
 */
export const formatTime = (date) => {
  return new Date(date).toLocaleTimeString(getLocale(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Format a message timestamp for display
 */
export const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * Format conversation time for sidebar display
 */
export const formatConversationTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString(getLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format last message time for conversation list
 * Shows translated "Today", "Yesterday", or the date
 */
export const formatConversationLastMessageTime = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();

  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isSameDay) {
    return formatConversationTime(timestamp);
  }

  if (isYesterday) {
    return i18n.t('dates.yesterday');
  }

  // More than a day ago – omit the year if it is the current year
  const isSameYear = date.getFullYear() === now.getFullYear();
  if (isSameYear) {
    return formatLocalDate(timestamp, { month: 'short', day: 'numeric' });
  }
  return formatDateShort(timestamp);
};

/**
 * Format "last seen" timestamp for user presence
 */
export const formatLastSeen = (timestamp) => {
  if (!timestamp) return i18n.t('dates.lastSeenRecently');

  const last = new Date(timestamp);
  const now = new Date();

  // Today - just show time
  if (last.toDateString() === now.toDateString()) {
    return i18n.t('dates.lastSeenAt', { time: formatTime(last) });
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (last.toDateString() === yesterday.toDateString()) {
    return i18n.t('dates.lastSeenYesterdayAt', { time: formatTime(last) });
  }

  // Same year - show day and month only
  if (last.getFullYear() === now.getFullYear()) {
    const dateStr = formatLocalDate(last, { day: 'numeric', month: 'short' });
    return i18n.t('dates.lastSeenDateAt', { date: dateStr, time: formatTime(last) });
  }

  // Different year - show full date
  const dateStr = formatLocalDate(last, { day: 'numeric', month: 'short', year: 'numeric' });
  return i18n.t('dates.lastSeenDateAt', { date: dateStr, time: formatTime(last) });
};

/**
 * Get relative time from now (e.g., "2 hours ago", "3 days ago")
 */
export const getRelativeTime = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) {
    return i18n.t('dates.justNow');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return i18n.t('dates.minutesAgo', { count: diffInMinutes });
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return i18n.t('dates.hoursAgo', { count: diffInHours });
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return i18n.t('dates.daysAgo', { count: diffInDays });
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return i18n.t('dates.weeksAgo', { count: diffInWeeks });
  }

  return formatDateShort(date);
};

